import { useState, type ReactNode } from 'react';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { APP_LABELS } from '../utils/labels';

export interface RowAction {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  color?: 'default' | 'primary' | 'warning' | 'error' | 'success' | 'inherit';
  className?: string;
  testId?: string;
}

interface RowActionsProps {
  actions: RowAction[];
}

export default function RowActions({ actions }: RowActionsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const showInline = actions.length <= 1 || !isMobile;

  if (showInline) {
    return (
      <>
        {actions.map((action) => (
          <Tooltip key={action.label} title={action.label}>
            <IconButton
              onClick={action.onClick}
              aria-label={action.label}
              color={action.color ?? 'default'}
              className={action.className}
              data-testid={action.testId}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        ))}
      </>
    );
  }

  return (
    <>
      <Tooltip title={APP_LABELS.moreActions}>
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={APP_LABELS.moreActions}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { className: 'min-w-40' } }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={() => { setAnchor(null); action.onClick(); }}
            data-testid={action.testId}
            sx={
              action.color && action.color !== 'default' && action.color !== 'inherit'
                ? { color: `${action.color}.main` }
                : undefined
            }
          >
            <span className="mr-3 flex items-center">{action.icon}</span>
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
